import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAssignSettingNewEmailUnsubscribePreferencePage, useSettingNewEmailUnsubscribePreferencePage } from "@faire/retailer-visitor-shared/settings/useSettingNewEmailUnsubscribePreferencePage";
import { BrandSentEmailPreferences } from "@faire/retailer-visitor-shared/views/settings/email-preferences/components/BrandSentEmailPreferences";
import { FaireSentEmailPreferences } from "@faire/retailer-visitor-shared/views/settings/email-preferences/components/FaireSentEmailPreferences";
import { useEmailPreferencesQuery } from "@faire/retailer-visitor-shared/views/settings/email-preferences/hooks/useEmailPreferencesQuery";
import { CONTENT_PADDING_LEFT } from "@faire/retailer-visitor-shared/views/settings/Layout";
import { SettingsSectionHeader } from "@faire/retailer-visitor-shared/views/settings/SettingsSectionHeader";
import { exhaustiveSwitchWithDefault } from "@faire/web--source/common/exhaustiveSwitchWithDefault";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web--source/common/localization/localize";
import { Path } from "@faire/web--source/common/Path";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Tabs } from "@faire/web--source/slate/Tabs";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackRetailerSettingsPageEmailPreferencesTabView } from "@faire/web-api--source/events/retailerSettingsPage/view/emailPreferencesTab";
import { QueryParameters as EmailPreferencesPageQueryParams } from "@faire/web-api--source/routes/retailer/settings/email-preferences";
import * as React from "react";
import { useCallback, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
var EmailPreferencesTab;
(function(EmailPreferencesTab) {
    EmailPreferencesTab["FAIRE_SENT"] = "FAIRE_SENT";
    EmailPreferencesTab["BRAND_SENT"] = "BRAND_SENT";
})(EmailPreferencesTab || (EmailPreferencesTab = {}));
export var EmailPreferencesForm = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var history = useHistory();
    var tabs = useEmailPreferencesTabs();
    var settingNewEmailUnsubscribePreferencePage = useSettingNewEmailUnsubscribePreferencePage();
    var tab = useQueryParams(EmailPreferencesPageQueryParams.parse).tab;
    var assignSettingNewEmailUnsubscribePreferencePage = useAssignSettingNewEmailUnsubscribePreferencePage();
    var emailPreferencesResponse = useEmailPreferencesQuery().emailPreferencesResponse;
    var emailPreferencesTitleRef = useRef(null);
    var handleTabChange = React.useCallback(function(newTab) {
        var path = new Path(history.location);
        path.replace({
            tab: newTab
        });
        if (newTab === EmailPreferencesTab.FAIRE_SENT) {
            // page param is specific to the brand-sent email tab, remove if navigating to faire-sent
            path.delete("page");
        }
        history.push(path.toString());
    }, [
        history
    ]);
    var scrollToTopOfTable = useCallback(function() {
        var _emailPreferencesTitleRef_current;
        (_emailPreferencesTitleRef_current = emailPreferencesTitleRef.current) === null || _emailPreferencesTitleRef_current === void 0 ? void 0 : _emailPreferencesTitleRef_current.scrollIntoView();
    }, []);
    useEffect(function() {
        assignSettingNewEmailUnsubscribePreferencePage();
    }, [
        assignSettingNewEmailUnsubscribePreferencePage
    ]);
    var _EmailPreferencesTab_tab;
    var activeTab = (_EmailPreferencesTab_tab = EmailPreferencesTab[tab]) !== null && _EmailPreferencesTab_tab !== void 0 ? _EmailPreferencesTab_tab : EmailPreferencesTab.FAIRE_SENT;
    return /*#__PURE__*/ _jsxs(FullWidthContainer, {
        children: [
            /*#__PURE__*/ _jsx(EmailPreferencesContentWrapper, {
                ref: emailPreferencesTitleRef,
                children: /*#__PURE__*/ _jsx(SettingsSectionHeader, {
                    title: strictLocalize({
                        id: "eMR/WP",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Email preferences"
                            }
                        ]
                    }),
                    description: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "7fMP/H",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Manage your email preferences for "
                            },
                            {
                                type: 1,
                                value: "email_address_to_display"
                            }
                        ],
                        values: DEPRECATED_backwardsCompatibleValues({
                            email_address_to_display: emailPreferencesResponse === null || emailPreferencesResponse === void 0 ? void 0 : emailPreferencesResponse.email_address_to_display
                        })
                    })
                })
            }),
            settingNewEmailUnsubscribePreferencePage ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: 4
                    }),
                    /*#__PURE__*/ _jsx(StyledTabs, {
                        tabs: tabs,
                        activeTab: activeTab,
                        onTabChange: handleTabChange
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "4x"
                    }),
                    /*#__PURE__*/ _jsx(TabContent, {
                        activeTab: activeTab,
                        scrollToTopOfTable: scrollToTopOfTable
                    })
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: 28
                    }),
                    /*#__PURE__*/ _jsx(FaireSentEmailPreferences, {})
                ]
            })
        ]
    });
};
var TabContent = function(param) {
    var activeTab = param.activeTab, scrollToTopOfTable = param.scrollToTopOfTable;
    useEffect(function() {
        trackRetailerSettingsPageEmailPreferencesTabView(activeTab);
    }, [
        activeTab
    ]);
    switch(activeTab){
        case EmailPreferencesTab.FAIRE_SENT:
            return /*#__PURE__*/ _jsx(FaireSentEmailPreferences, {});
        case EmailPreferencesTab.BRAND_SENT:
            return /*#__PURE__*/ _jsx(BrandSentEmailPreferences, {
                scrollToTopOfTable: scrollToTopOfTable
            });
        default:
            return exhaustiveSwitchWithDefault(null)(activeTab);
    }
};
var useEmailPreferencesTabs = function() {
    var strictLocalize = useStrictLocalization().strictLocalize;
    return [
        {
            value: EmailPreferencesTab.FAIRE_SENT,
            label: strictLocalize({
                id: "OZj4sj",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Emails from Faire"
                    }
                ]
            })
        },
        {
            value: EmailPreferencesTab.BRAND_SENT,
            label: strictLocalize({
                id: "ialHpF",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Emails from brands"
                    }
                ]
            })
        }
    ];
};
var FullWidthContainer = styled(Column).withConfig({
    componentId: "sc-ddd9e0f4-0"
})([
    "width:100%;"
]);
export var EmailPreferencesContentWrapper = styled.section.withConfig({
    componentId: "sc-ddd9e0f4-1"
})([
    "max-width:800px;width:100%;"
]);
var StyledTabs = styled(Tabs).withConfig({
    componentId: "sc-ddd9e0f4-2"
})([
    "",
    "{position:relative;left:-",
    ";> div > div:first-child{padding-left:",
    ";}}"
], responsiveMediaQueries.tabletAndAbove, CONTENT_PADDING_LEFT, CONTENT_PADDING_LEFT);
